import { useEffect, FC, useRef } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Box,
  Paper,
  Chip,
} from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import WeightNote from '../../../Models/WeightNoteModels/WeightNote';
import Landing from '../../../Models/LandingModels/Landing';
import { getLandingById } from '../../../Services/LondunarkerfiAPIService';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { FilterPayload } from './Filters/FilterDefinition';


interface WeightNoteReportProps {
  apiRef: React.MutableRefObject<GridApiCommunity>;
  toggleDashboardOpen: (open: boolean) => void;
  onLandingSelect: (landing: Landing, newLandingId?: number, landings?: Landing[], newWeightNoteId?: number) => void;
  search: string;
  setSearch: (val: string) => void;
  loading: boolean;
  weightNotes: WeightNote[];
  fetchWeightNotes: (
    pageToFetch: number,
    searchText: string,
    reset: boolean,
    filterModel?: FilterPayload
  ) => Promise<void>;
  hasMore: boolean;
  setPage: (val: (prev: number) => number) => void;
}

const WeightNoteReport: FC<WeightNoteReportProps> = (props) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleWeightNoteSelection = async (weightNote: WeightNote) => {
    const landing = await getLandingById(weightNote.landingId);
    props.onLandingSelect(landing, landing.id, undefined, weightNote.id);
    props.toggleDashboardOpen(false);
  };

  // Custom infinite scroll handler
  useEffect(() => {
    const gridNode = gridRef.current;
    if (!gridNode) return;

    const handleScroll = () => {
      if (props.loading || !props.hasMore) return;
      const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
      if (!gridWindow) return;
      const { scrollTop, scrollHeight, clientHeight } = gridWindow as HTMLElement;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        // Near bottom, fetch next page
        props.setPage(prev => prev + 1);
      }
    };

    const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
    if (gridWindow) {
      gridWindow.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (gridWindow) {
        gridWindow.removeEventListener('scroll', handleScroll);
      }
    };
  }, [props]);

  const columns: GridColDef[] = [
    {
      field: 'noteNumber',
      headerName: 'Númer',
      flex: 0.7,
      minWidth: 80,
      sortable: true,
    },
    {
      field: 'weightNoteRegistered',
      headerName: 'Skráningartími',
      flex: 1,
      minWidth: 100,
      type: 'date',
      valueGetter: (params) => {
        const date = params.row.weightNoteRegistered;
        return date ? new Date(date) : null;
      },
      valueFormatter: (params) => {
        return params.value ? dayjs(params.value).format('DD.MM.YYYY HH:mm') : '';
      }, renderCell: (params) => {
        const date = params.row.weightNoteRegistered
          ? dayjs(params.row.weightNoteRegistered).format('DD.MM.YYYY HH:mm')
          : '';
        return <>{date}</>;
      },
      sortable: true,
    },
    {
      field: 'buyerName',
      headerName: 'Kaupandi',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.row.buyer?.name,
      sortable: true,
    },
    {
      field: 'receiverName',
      headerName: 'Viðtakandi',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.row.receiver?.name,
      sortable: true,
    },
    {
      field: 'totalQuantity',
      headerName: 'Heildarmagn',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.totalQuantity,
      sortable: true,
      type: 'number',
    },
    {
      field: 'gearName',
      headerName: 'Veiðarfæri',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.gear?.name,
      sortable: true,
    },
    {
      field: 'weightNoteTypeName',
      headerName: 'Tegund',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.weightNoteType?.name,
      sortable: true,
    },
    {
      field: 'isClosed',
      headerName: 'Lokuð',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const isClosed = params.row.isClosed;
        const chipLabel = isClosed ? "Lokuð" : "Opin";
        const chipColor: "success" | "warning" = isClosed ? "success" : "warning";
        return (
          <Chip
            label={chipLabel}
            color={chipColor}
            size="small"
            sx={{ fontWeight: 500, borderRadius: 1 }}
          />
        );
      },
      sortable: false,
      type: 'boolean',
      valueGetter: (params) => params.row.isClosed
    },
    {
      field: 'isCredit',
      headerName: 'Er kredit',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        params.row.isCredit
          ? <span title="Kredit" style={{ fontSize: 18, color: '#1976d2' }}>✔️</span>
          : <span title="Ekki kredit" style={{ fontSize: 18, color: '#bdbdbd' }}>✖️</span>
      ),
      sortable: false,
      type: 'boolean',
      valueGetter: (params) => params.row.isCredit
    },
  ];

  return (
    <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexGrow: 1, height: '100%', minHeight: '400px' }}>
        <div ref={gridRef} style={{ height: '100%' }}>
          <DataGrid
            apiRef={props.apiRef}
            disableColumnFilter
            rows={props.weightNotes}
            columns={columns}
            loading={props.loading}
            density="standard"
            getRowHeight={() => 'auto'}
            initialState={{
              sorting: {
                sortModel: [{ field: 'weightNoteDate', sort: 'desc' }],
              },
            }}
            onRowClick={(params) => {
              handleWeightNoteSelection(params.row);
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-paper': {
                minWidth: 380,
                maxWidth: 420,
              },
              '& .MuiDataGrid-filterForm': {
                flexDirection: 'column',
                alignItems: 'stretch',
                width: '100%',
                gap: 2,
              },
            }}
            localeText={{
              toolbarFilters: "Síur",
              toolbarFiltersTooltipHide: "Fela síur",
              toolbarFiltersTooltipShow: "Sýna síur",
              columnHeaderFiltersLabel: "Sía eftir",
              filterOperatorContains: "Inniheldur",
              filterOperatorEquals: "Jafnt og",
              filterOperatorStartsWith: "Byrjar á",
              filterOperatorEndsWith: "Endar á",
              filterOperatorIs: "Er",
              filterOperatorNot: "Er ekki",
              filterOperatorAfter: "Eftir",
              filterOperatorOnOrAfter: "Á eða eftir",
              filterOperatorBefore: "Fyrir",
              filterOperatorOnOrBefore: "Á eða fyrir",
              filterOperatorIsEmpty: "Er tómt",
              filterOperatorIsNotEmpty: "Er ekki tómt",
              filterValueAny: "(einhver)",
              filterValueTrue: "satt",
              filterValueFalse: "ósatt",
              filterOperatorIsAnyOf: "Er einhver af",
              toolbarColumns: "Dálkar",
              toolbarColumnsLabel: "Dálkar",
              columnsPanelTextFieldLabel: "Finna dálk",
              columnsPanelTextFieldPlaceholder: "Leita að dálki",
              columnsPanelDragIconLabel: "Endurraða dálkum",
              columnsPanelShowAllButton: "Sýna alla",
              columnsPanelHideAllButton: "Fela alla",
              filterPanelColumns: "Dálkar",
              filterPanelOperator: "Skilyrði",
              filterPanelInputLabel: "Gildi",
              filterPanelInputPlaceholder: "Sía gildi",
              filterPanelAddFilter: "Bæta við síu",
              filterPanelDeleteIconLabel: "Eyða",
              columnMenuLabel: "Valmynd",
              columnMenuShowColumns: "Sýna dálka",
              columnMenuFilter: "Sía",
              columnMenuHideColumn: "Fela",
              columnMenuUnsort: "Hætta að raða",
              columnMenuSortAsc: "Raða hækkandi",
              columnMenuSortDesc: "Raða lækkandi",
              columnMenuManageColumns: "Stjórna dálkum",
            }}
          />
        </div>
      </Box>
    </Paper>
  );
};

export default WeightNoteReport;